import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  Edit3,
  MessageSquare,
  MoreHorizontal,
  Plus,
  Trash2,
  X
} from 'lucide-react';
import Message from './Message';
import Input from './Input';
import Interests from './Interests';
import Suggestions from './Suggestions';
import TutorSelector from './TutorSelector';
import { apiAIFetch, apiFetch } from '../api';

const titleFromMessage = (text) => {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return 'New chat';
  return clean.length > 60 ? `${clean.slice(0, 57).trim()}...` : clean;
};

const formatConversationTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const Chat = ({ onCostUpdate, onCostReset }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const [compact, setCompact] = useState(() => {
    const saved = localStorage.getItem('lrbrain-compact-mode');
    return saved ? saved === 'true' : false;
  });
  const [stepByStep, setStepByStep] = useState(() => {
    const saved = localStorage.getItem('lrbrain-step-by-step');
    return saved ? saved === 'true' : true;
  });
  const [gradeLevel, setGradeLevel] = useState(() => localStorage.getItem('lrbrain-grade-level') || 'Auto');
  const [notesOpen, setNotesOpen] = useState(false);
  const [starred, setStarred] = useState(() => {
    try {
      const raw = localStorage.getItem('lrbrain-starred');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [selectedTutor, setSelectedTutor] = useState(null);

  const hasActiveChat = Boolean(activeConversation);
  const messageCountLabel = useMemo(() => {
    if (!messages.length) return '';
    return `${messages.length} message${messages.length === 1 ? '' : 's'}`;
  }, [messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    loadConversations();
    // The initial load should run once; follow-up refreshes are explicit after mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyCostData = (data) => {
    if (!onCostUpdate) return;
    const costValue = typeof data?.cost_data === 'number'
      ? data.cost_data
      : data?.cost_data?.total_cost;
    if (typeof costValue === 'number' && costValue > 0) {
      onCostUpdate(costValue);
    } else if (typeof data?.cost === 'number' && data.cost > 0) {
      onCostUpdate(data.cost);
    }
  };

  const loadConversations = async ({ selectFirst = true } = {}) => {
    setIsHistoryLoading(true);
    try {
      const response = await apiFetch('/api/chat/conversations');
      if (!response.ok) throw new Error(`Could not load conversations (${response.status})`);
      const data = await response.json();
      const items = data.conversations || [];
      setConversations(items);

      if (selectFirst && items.length > 0) {
        await selectConversation(items[0].id, items[0]);
      } else if (items.length === 0) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Error loading chat conversations:', err);
      setError('Could not load your chat history.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const selectConversation = async (conversationId, knownConversation = null) => {
    setError(null);
    try {
      const response = await apiFetch(`/api/chat/conversations/${conversationId}`);
      if (!response.ok) throw new Error(`Could not load chat (${response.status})`);
      const data = await response.json();
      setActiveConversation(data.conversation || knownConversation);
      setMessages(data.history || []);
    } catch (err) {
      console.error('Error loading chat:', err);
      setError('Could not open that chat.');
    }
  };

  const newChat = () => {
    setActiveConversation(null);
    setMessages([]);
    setError(null);
  };

  const refreshConversationList = async (selectedId = activeConversation?.id) => {
    try {
      const response = await apiFetch('/api/chat/conversations');
      if (!response.ok) return;
      const data = await response.json();
      const items = data.conversations || [];
      setConversations(items);
      if (selectedId) {
        const selected = items.find((item) => item.id === selectedId);
        if (selected) setActiveConversation(selected);
      }
    } catch (err) {
      console.error('Error refreshing conversations:', err);
    }
  };

  const createConversation = async (title = 'New chat') => {
    const response = await apiFetch('/api/chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title })
    });
    if (!response.ok) throw new Error(`Could not create chat (${response.status})`);
    const data = await response.json();
    return data.conversation;
  };

  const sendMessage = async (text) => {
    const optimisticUser = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    setIsLoading(true);
    setError(null);
    setMessages((prev) => [...prev, optimisticUser]);

    try {
      let conversation = activeConversation;
      if (!conversation) {
        conversation = await createConversation(titleFromMessage(text));
        setActiveConversation(conversation);
      }

      const response = await apiAIFetch(
        `/api/chat/conversations/${conversation.id}/messages`,
        'chat.send',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text,
            selected_tutor_id: selectedTutor?.id || null,
            options: {
              step_by_step: stepByStep,
              grade_level: gradeLevel,
              compact_mode: compact
            }
          })
        }
      );

      if (response.status === 429) {
        setError('Rate limit reached. Please try again shortly.');
        return;
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || `Request failed (${response.status})`);
      }

      const data = await response.json();
      applyCostData(data);
      setMessages(data.history || []);
      setActiveConversation(data.conversation || conversation);
      await refreshConversationList(data.conversation_id || conversation.id);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(`Something went wrong sending that message. ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renameConversation = async (conversation, event) => {
    event.stopPropagation();
    const nextTitle = window.prompt('Rename chat', conversation.title || 'New chat');
    if (nextTitle === null) return;
    const trimmed = nextTitle.trim();
    if (!trimmed) return;

    try {
      const response = await apiFetch(`/api/chat/conversations/${conversation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: trimmed })
      });
      if (!response.ok) throw new Error(`Rename failed (${response.status})`);
      await refreshConversationList(conversation.id);
    } catch (err) {
      console.error('Error renaming conversation:', err);
      setError('Could not rename that chat.');
    }
  };

  const archiveConversation = async (conversation, event) => {
    event.stopPropagation();
    try {
      const response = await apiFetch(`/api/chat/conversations/${conversation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'ARCHIVED' })
      });
      if (!response.ok) throw new Error(`Archive failed (${response.status})`);
      const remaining = conversations.filter((item) => item.id !== conversation.id);
      setConversations(remaining);
      if (activeConversation?.id === conversation.id) {
        if (remaining[0]) {
          await selectConversation(remaining[0].id, remaining[0]);
        } else {
          newChat();
        }
      }
    } catch (err) {
      console.error('Error archiving conversation:', err);
      setError('Could not archive that chat.');
    }
  };

  const clearChat = async () => {
    if (!activeConversation) return;
    try {
      const response = await apiFetch('/api/clear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation_id: activeConversation.id })
      });
      if (!response.ok) throw new Error(`Clear failed (${response.status})`);
      const data = await response.json();
      setMessages([]);
      setError(null);
      setActiveConversation(data.conversation || activeConversation);
      await refreshConversationList(activeConversation.id);
      if (onCostReset) onCostReset();
    } catch (err) {
      console.error('Error clearing chat:', err);
      setError('Could not clear this chat.');
    }
  };

  const toggleStar = (index) => {
    setStarred((prev) => {
      const message = messages[index];
      if (!message) return prev;
      const key = message.id || message.timestamp || index;
      const exists = prev.find((item) => item.key === key);
      const next = exists
        ? prev.filter((item) => item.key !== key)
        : [...prev, { key, role: message.role, content: message.content, ts: message.timestamp || key }];
      localStorage.setItem('lrbrain-starred', JSON.stringify(next));
      return next;
    });
  };

  const exportNotes = () => {
    const lines = starred.map((item, index) => `### Note ${index + 1} (${item.role})\n\n${item.content}\n`);
    const blob = new Blob([`# Starred Notes\n\n${lines.join('\n')}`], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'lrbai-notes.md';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`lrbrain-chat-container${compact ? ' compact' : ''}`}>
      <aside className="lrbrain-chat-sidebar" aria-label="Chat history">
        <button type="button" className="lrbrain-new-chat-btn" onClick={newChat}>
          <Plus size={18} />
          <span>New Chat</span>
        </button>

        <div className="lrbrain-chat-history-heading">Conversation History</div>
        <div className="lrbrain-chat-thread-list">
          {isHistoryLoading ? (
            <div className="lrbrain-chat-history-empty">Loading chats...</div>
          ) : conversations.length === 0 ? (
            <div className="lrbrain-chat-history-empty">No saved chats yet.</div>
          ) : (
            conversations.map((conversation) => {
              const isActive = activeConversation?.id === conversation.id;
              return (
                <div
                  key={conversation.id}
                  role="button"
                  tabIndex={0}
                  className={`lrbrain-chat-thread ${isActive ? 'active' : ''}`}
                  onClick={() => selectConversation(conversation.id, conversation)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectConversation(conversation.id, conversation);
                    }
                  }}
                >
                  <MessageSquare size={18} />
                  <span className="thread-copy">
                    <span className="thread-title">{conversation.title || 'New chat'}</span>
                    <span className="thread-meta">
                      {formatConversationTime(conversation.last_message_at || conversation.updated_at)}
                    </span>
                  </span>
                  <span className="thread-actions">
                    <button
                      type="button"
                      title="Rename chat"
                      aria-label="Rename chat"
                      onClick={(event) => renameConversation(conversation, event)}
                    >
                      <Edit3 size={14} />
                    </button>
                    <button
                      type="button"
                      title="Archive chat"
                      aria-label="Archive chat"
                      onClick={(event) => archiveConversation(conversation, event)}
                    >
                      <Archive size={14} />
                    </button>
                  </span>
                </div>
              );
            })
          )}
        </div>
      </aside>

      <section className="lrbrain-chat-main">
        <TutorSelector onTutorSelect={setSelectedTutor} selectedTutor={selectedTutor} />

        {selectedTutor && (
          <div className="tutor-filter-active-banner">
            <span>Viewing as: <strong>{selectedTutor.name}</strong></span>
            <button onClick={() => setSelectedTutor(null)} className="clear-filter">
              Clear
            </button>
          </div>
        )}

        <div className="lrbrain-controls-bar">
          <div className="left">
            <label className="toggle">
              <input
                type="checkbox"
                checked={compact}
                onChange={(event) => {
                  setCompact(event.target.checked);
                  localStorage.setItem('lrbrain-compact-mode', String(event.target.checked));
                }}
              />
              <span>Compact</span>
            </label>
            <label className="toggle">
              <input
                type="checkbox"
                checked={stepByStep}
                onChange={(event) => {
                  setStepByStep(event.target.checked);
                  localStorage.setItem('lrbrain-step-by-step', String(event.target.checked));
                }}
              />
              <span>Step-by-step</span>
            </label>
            <label className="select">
              <span>Grade:</span>
              <select
                value={gradeLevel}
                onChange={(event) => {
                  setGradeLevel(event.target.value);
                  localStorage.setItem('lrbrain-grade-level', event.target.value);
                }}
              >
                {['Auto', 'Middle School', 'High School', 'College', 'Graduate'].map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="right">
            <Interests />
            <button type="button" className="notes-btn" onClick={() => setNotesOpen(true)}>
              Notes ({starred.length})
            </button>
          </div>
        </div>

        {error && (
          <div className="lrbrain-chat-error" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} aria-label="Dismiss error">
              <X size={16} />
            </button>
          </div>
        )}

        {!hasActiveChat && messages.length === 0 ? (
          <div className="lrbrain-chat-empty-state">
            <div className="lrbrain-chat-empty-copy">
              <h1>How can I help with learning today?</h1>
              <p>Ask about a session, create practice, search summaries, or plan the next step.</p>
            </div>
            <Suggestions onSelect={sendMessage} compact />
            <Input
              onSendMessage={sendMessage}
              isLoading={isLoading}
              variant="hero"
              placeholder="Type a coaching prompt or ask about a student..."
            />
          </div>
        ) : (
          <>
            <div className="message-history">
              {messages.map((message, index) => (
                <Message
                  key={message.id || `${message.timestamp}-${index}`}
                  role={message.role}
                  content={message.content}
                  timestamp={message.timestamp || new Date()}
                  onStar={() => toggleStar(index)}
                  isStarred={!!starred.find((item) => item.key === (message.id || message.timestamp || index))}
                  compact={compact}
                />
              ))}

              {isLoading && (
                <div className="lrbrain-message-container ai">
                  <img
                    src="/lrbrain-logo.png"
                    alt="LRB thinking"
                    className={`lrbrain-mascot-avatar spinning${compact ? ' small' : ''}`}
                  />
                  <div className="lrbrain-message-content">
                    <div className="lrbrain-message ai lrbrain-typing">
                      <div className="lrbrain-typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <div className="lrbrain-thinking-label">Thinking, please wait...</div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <Suggestions onSelect={sendMessage} compact />

            <div className="lrbrain-chat-footer-row">
              <span>{messageCountLabel}</span>
              {messages.length > 0 && (
                <button type="button" onClick={clearChat} className="lrbrain-clear-chat-btn">
                  <Trash2 size={14} />
                  Clear Chat
                </button>
              )}
            </div>

            <Input
              onSendMessage={sendMessage}
              isLoading={isLoading}
              placeholder="Ask a learning question..."
            />
          </>
        )}
      </section>

      {notesOpen && (
        <div className="lrbrain-notes-drawer" role="dialog" aria-modal="true">
          <div className="notes-header">
            <div className="title">Starred Notes</div>
            <div className="spacer" />
            <button className="ghost" onClick={exportNotes} title="Export to Markdown">Export</button>
            <button className="close" onClick={() => setNotesOpen(false)} aria-label="Close">x</button>
          </div>
          <div className="notes-body">
            {starred.length === 0 ? (
              <div className="empty">No notes yet. Star any message to save it here.</div>
            ) : (
              starred.map((item, index) => (
                <div key={`${item.ts}-${index}`} className="note-item">
                  <div className={`badge ${item.role === 'assistant' ? 'ai' : item.role}`}>{item.role}</div>
                  <div className="content">{item.content}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <MoreHorizontal className="lrbrain-chat-mobile-grip" size={18} aria-hidden="true" />
    </div>
  );
};

export default Chat;
